import type { TokenCredential } from '@azure/identity';
import ky, { type BeforeRequestHook, type KyInstance } from 'ky';

// Deep import: the backend barrel imports back into `lib`, so going through
// it from here would create a dependency cycle.
import { IntegrationError } from '../../backend/integration-error';

/**
 * An authed HTTP client for one Microsoft surface (Graph or ARM). Responses
 * pass through with their real HTTP status, so callers own error semantics:
 * the integration proxy maps them itself, and checks classify them through
 * their handler's `handleErrors`. Token caching and refresh live inside the
 * credential (MSAL), so fetching per request is the SDK-blessed pattern.
 */
export function createMicrosoftBearerKy(input: {
  credential: TokenCredential;
  scope: string;
  prefix: string;
}): KyInstance {
  return ky.create({
    prefix: input.prefix,
    retry: 4,
    hooks: {
      beforeRequest: [bearerTokenHook(input.credential, input.scope)],
    },
  });
}

function bearerTokenHook(credential: TokenCredential, scope: string): BeforeRequestHook {
  return async ({ request }) => {
    let token;
    try {
      token = await credential.getToken(scope);
    } catch (error) {
      throw IntegrationError.unauthenticated({ cause: error });
    }
    if (!token) {
      throw IntegrationError.unauthenticated();
    }
    request.headers.set('authorization', `Bearer ${token.token}`);
  };
}
